<template>
  <div
    id="timeline-wrapper"
    ref="timelineWrapper"
    class="timeline-wrapper"
  >
    <div
      :class="{ 'show-total-hours': showTotalHours, loading: rowsLoading, 'timeline-grid-user': isUserMode }"
      class="timeline-grid"
    >
      <!-- Header -->
      <div
        class="grid-item timeline-header timeline-label-column d-flex align-items-center cursor-pointer gap-5"
        @click="toggleSort(isUserMode ? 'date' : 'member')"
      >
        <span
          v-if="sortArrow(isUserMode ? 'date' : 'member')"
          class="material-symbols-rounded bold hi-14"
        >
          {{ sortArrow(isUserMode ? 'date' : 'member') }}
        </span>

        {{ isUserMode ? userData?.name || 'Date' : 'Member' }}
      </div>
      <template v-if="showTotalHours">
        <div
          class="grid-item timeline-header d-flex align-items-center gap-5"
          :class="{ 'cursor-pointer': !isUserMode }"
          @click="() => !isUserMode && toggleSort('total_time')"
        >
          <span
            v-if="!isUserMode && sortArrow('total_time')"
            class="material-symbols-rounded bold hi-14"
          >
            {{ sortArrow('total_time') }}
          </span>

          Total hours
          <div class="pr-10"></div>
        </div>
      </template>
      <div
        v-for="hour in hours"
        :key="`header-${hour}`"
        class="grid-item timeline-header"
      >
        <div
          v-if="showTotalHours || hour"
          class="timeline-hour"
        >
          {{ hour }}:00
        </div>
      </div>

      <!-- Rows -->
      <template
        v-for="(row, index) in data"
        :key="`row-${row.id}`"
      >
        <div
          :class="index === 0 ? 'first' : ''"
          class="grid-item timeline-cell timeline-label-column flex"
        >
          <template v-if="!isUserMode">
            <div
              class="user-status user-status--speck mr-10"
              :class="{ 'is-online': row.active_now }"
            >
              <AppAvatar
                v-if="row.avatar"
                :avatar="row.avatar"
                size="speck"
              />
            </div>
            <a
              v-tooltip
              :title="`View ${row.name}'s timeline`"
              :href="memberTimelineUrl(row)"
              class="ellipsis skeleton cursor-pointer"
              @click="handleMemberClick(row, $event)"
            >
              {{ row.name }}
            </a>
          </template>
          <div
            v-else
            class="ellipsis skeleton"
          >
            {{ formatDate(row.name) }}
          </div>
        </div>
        <div
          v-if="showTotalHours"
          :class="['grid-item timeline-cell justify-content-center', { first: index === 0 }]"
        >
          <span class="skeleton">
            {{ formatTime(row.total_time) }}
          </span>
        </div>
        <div
          :ref="`span-${row.id}`"
          :class="['grid-item timeline-span-24 relative', { first: index === 0 }]"
        >
          <InsightsTimelineSvg
            v-if="width"
            :loading="loading"
            :date="row.date || date"
            :selected-task-id="selectedUserId === row.id ? selectedTaskId : null"
            :svg-width="width"
            :time-data="userTimeData[row.id] || []"
            :time-zone="timeZone"
            @task-clicked="handleTaskClicked"
            @width-mismatch="handleWidthMismatch"
          />
          <InsightsCurrentTimeLine
            v-if="showCurrentTimeLine"
            :time-zone="timeZone"
            :tooltip="'Live activity will show after a delay of 10 mins or more'"
          />
        </div>
      </template>
    </div>

    <!-- Centralized popover for all timelines -->
    <AppChartPopover
      v-if="selectedTaskId && popover?.data"
      ref="timelinePopover"
      :column-data="popover.data.taskData"
      :position="popover.position"
      :sticky-column-width="230"
      :top-reserved-space="60"
      strategy="container"
      trigger="click"
      custom-class="timeline-popover"
      to="#timeline-wrapper"
      tabindex="0"
      @close="clearSelection"
    >
      <template #popover-header>
        <div class="d-flex align-items-center">
          <div :class="`${popover.data.taskData.type} row-activity-circle mr-10`" />
          <b>
            {{ getActivityTypeLabel(popover.data.taskData.type) }}
          </b>
        </div>
      </template>
      <template #popover-content>
        <div class="d-flex align-items-center gap-20 text-nowrap">
          <div>
            {{ formatTaskTime(popover.data.taskData.start_date) }} -
            {{ formatTaskTime(popover.data.taskData.end_date) }}
            <span class="z-text-gray-500">
              ({{ formatTaskDuration(popover.data.taskData.end_date, popover.data.taskData.start_date) }})
            </span>
          </div>
          <div
            v-if="getStatusText(popover.data.taskData)"
            class="timeline-status-badge"
            :class="getStatusClass(popover.data.taskData)"
          >
            {{ getStatusText(popover.data.taskData) }}
          </div>
        </div>
        <div
          v-if="popover.data.taskData.project_names?.length"
          class="timeline-popover-divider"
        >
          <label class="d-block control-label">Projects</label>
          <div
            v-for="project in popover.data.taskData.project_names"
            :key="project"
          >
            {{ project }}
          </div>
        </div>
        <div
          v-if="screenshotCount"
          class="timeline-popover-divider"
        >
          <InsightsViewFullDayLink
            :user-id="popover.data.taskData.user_id_for_screens"
            :organization-id="$store.state.organization.id"
            :date="popover.data.taskData.start_date"
            :time-zone="timeZone"
            event="Timeline screenshot count clicked"
            class="screen-count-pill"
          >
            {{ screenshotCount }} {{ $filters.pluralize(screenshotCount, 'screen', 'screens') }}
          </InsightsViewFullDayLink>
        </div>
      </template>
    </AppChartPopover>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AppChartPopover from '~/components/AppChartPopover.vue';
import InsightsCurrentTimeLine from './InsightsCurrentTimeLine.vue';
import InsightsTimelineSvg from './InsightsTimelineSvg.vue';
import InsightsViewFullDayLink from './InsightsViewFullDayLink.vue';
import { formatDuration } from '~/utils/time_utils';
import { INSIGHTS } from '~/app_constants';
import { ScreensResource } from '~/resources';
import { InsightsTracking } from '~/mixins';

dayjs.extend(utc);

const HOURS = [...Array(24).keys()];
// Mapping between UI metric names and data keys returned by API
const METRIC_DATA_MAPPING = {
  tracked: 'active_time', // When 'tracked' is selected, API returns data as 'active_time'
};

export default {
  name: 'InsightsTimelineChart',
  components: {
    InsightsTimelineSvg,
    InsightsCurrentTimeLine,
    AppChartPopover,
    InsightsViewFullDayLink,
  },
  mixins: [InsightsTracking],
  props: {
    date: { type: String, required: true },
    data: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    rowsLoading: { type: Boolean, default: false },
    showTotalHours: { type: Boolean, required: true },
    selectedMetrics: { type: Array, default: () => [] },
    timeZone: { type: String, required: true },
    sort: {
      type: Object,
      default: () => ({ sort_by: 'member', sort_order: 'asc' }),
    },
    userData: { type: Object, default: null },
  },
  data() {
    return {
      hours: HOURS,
      width: null,
      popover: null,
      selectedTaskId: null,
      resizeTimeout: null,
      selectedUserId: null,
      timeGapsCache: null,
      lastTaskData: null,
      isDocListenerAttached: false,
      screenshotCount: null,
      updateChartTimer: null,
    };
  },
  computed: {
    showCurrentTimeLine() {
      return this.width > 900;
    },
    isUserMode() {
      return !!this.$store.getters.userId;
    },
    computedTimeGaps() {
      // Process metrics data to handle API naming differences
      const dataWithMetrics = this.data.map((row) => {
        const processedMetrics = { ...row.metrics };

        // Map API data keys to UI metric names
        this.selectedMetrics.forEach((metric) => {
          const apiDataKey = METRIC_DATA_MAPPING[metric];

          if (apiDataKey && row.metrics?.[apiDataKey]) {
            processedMetrics[`${metric}_time`] = row.metrics[apiDataKey];
          }
        });

        return { ...row, metrics: processedMetrics };
      });
      const timeGaps = this.groupConsecutiveIntervals(dataWithMetrics).flatMap((row) => {
        if (!row.metrics) {
          return [];
        }

        return Object.entries(row.metrics).flatMap(([type, intervals]) => {
          if (!intervals || !Array.isArray(intervals)) {
            return [];
          }

          return intervals.map((item, index) => {
            const taskData = {
              id: `${row.id}-${type}-${item.starts_at || ''}-${index}`,
              start_date: item.starts_at,
              end_date: item.ends_at,
              project_names: item.project_names,
              project_ids: item.project_ids || [],
              type: type.replace('_time', ''),
              user_id: row.id,
              user_id_for_screens: item.user_id,
              status: item.status,
              paid: item.paid,
              ...(item.activity_levels && { areaData: item.activity_levels }),
            };

            return taskData;
          });
        });
      });

      return timeGaps;
    },
    // O(n) indexing instead of O(n*m) nested filter for large datasets
    userTimeData() {
      if (!this.selectedMetrics.length) {
        return {};
      }
      const gaps = this.timeGapsCache || this.computedTimeGaps;
      const selectedSet = new Set(this.selectedMetrics);
      const indexed = {};

      for (let i = 0; i < this.data.length; i++) {
        indexed[this.data[i].id] = [];
      }
      for (let i = 0; i < gaps.length; i++) {
        const gap = gaps[i];

        if (!selectedSet.has(gap.type)) {
          continue;
        }
        if (!indexed[gap.user_id]) {
          continue;
        }
        indexed[gap.user_id].push(gap);
      }

      return indexed;
    },
  },
  watch: {
    data: {
      handler() {
        this.timeGapsCache = null;
        this.scheduleUpdate();
      },
      immediate: true,
    },
    showTotalHours() {
      this.clearSelection();
      this.scheduleUpdate();
    },
    selectedMetrics() {
      this.clearSelection();
      this.timeGapsCache = null;
      this.scheduleUpdate();
    },
    date() {
      this.clearSelection();
      this.scheduleUpdate();
      this.scrollToTop();
    },
  },
  mounted() {
    this.addEventListeners();
    this.updateChart();
  },
  beforeUnmount() {
    this.removeEventListeners();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.updateChartTimer) {
      cancelAnimationFrame(this.updateChartTimer);
    }
  },
  methods: {
    addEventListeners() {
      const timelineWrapper = this.$refs.timelineWrapper;

      if (timelineWrapper) {
        timelineWrapper.addEventListener('scroll', this.handleScroll);
        this._resizeObserver = new ResizeObserver(this.handleResize);
        this._resizeObserver.observe(timelineWrapper);
      }
    },
    removeEventListeners() {
      document.removeEventListener('click', this.handleDocumentClick);
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      const timelineWrapper = this.$refs.timelineWrapper;

      if (timelineWrapper) {
        timelineWrapper.removeEventListener('scroll', this.handleScroll);
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    getActivityTypeLabel(type) {
      return INSIGHTS.timeline.metrics[type].title;
    },
    capitalizeStatus(status) {
      if (!status) {
        return '';
      }

      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    getStatusClass(taskData) {
      if (!taskData) {
        return '';
      }

      if (this.hasOverlappingMeeting(taskData)) {
        return 'status-meeting';
      }
      if (taskData.paid !== undefined) {
        return `status-${taskData.paid ? 'paid' : 'unpaid'}`;
      }
      if (taskData.status) {
        return `status-${taskData.status.toLowerCase()}`;
      }

      return '';
    },
    getStatusText(taskData) {
      if (this.hasOverlappingMeeting(taskData)) {
        return 'In a meeting';
      }
      if (taskData.paid !== undefined) {
        return taskData.paid ? 'Paid' : 'Unpaid';
      }
      if (taskData.status) {
        return this.capitalizeStatus(taskData.status);
      }

      return '';
    },
    hasOverlappingMeeting(taskData) {
      if (!this.selectedMetrics.includes('meetings')) {
        return false;
      }

      const taskStart = dayjs(taskData.start_date);
      const taskEnd = dayjs(taskData.end_date);
      const userRow = this.data.find((row) => row.id === taskData.user_id);

      if (!userRow?.metrics?.meetings) {
        return false;
      }

      // Check if any meeting intersects with the task time
      return userRow.metrics.meetings.some((meeting) => {
        const meetingStart = dayjs(meeting.starts_at);
        const meetingEnd = dayjs(meeting.ends_at);

        // Check for intersection: meeting starts before task ends AND meeting ends after task starts
        return meetingStart.isBefore(taskEnd) && meetingEnd.isAfter(taskStart);
      });
    },
    handleResize() {
      this.clearSelection();
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      // Wait 600ms to allow both sidebar (500ms) and page content (500ms) transitions to complete
      // Safari needs extra time after transitionend for layout to fully stabilize before measuring
      // Using 150ms causes chart to be stuck at intermediate width during transition
      this.resizeTimeout = setTimeout(() => {
        this.scheduleUpdate();
      }, 600);
    },
    handleScroll() {
      // Since the popover is placed INSIDE the scrollable container with absolute positioning,
      // it automatically moves with the content when scrolling. No position update needed.
      // We can optionally close the popover on scroll if desired, or just let it move naturally.
      // Option 1: Keep popover open (do nothing)
      // The popover will scroll with the content
      // Option 2: Close popover on scroll (uncomment below)
      // if (this.selectedTaskId) {
      //   this.clearSelection();
      // }
    },
    handleDocumentClick(event) {
      // Ignore clicks on task elements that should open popovers
      const isTaskElement = event.target.closest('.row-activity') || event.target.classList?.contains('row-activity');

      if (isTaskElement) {
        return;
      }

      // Close popover if click is outside
      const popover = this.$refs.timelinePopover?.$el;

      if (popover && !popover.contains(event.target)) {
        this.clearSelection();
      }
    },
    restorePopover() {
      if (this.lastTaskData) {
        this.handleTaskClicked(this.lastTaskData);
      }
    },
    handleTaskClicked(data) {
      // Toggle selection if clicking the same task
      if (this.selectedUserId === data.taskData.user_id && this.selectedTaskId === data.taskId) {
        return this.clearSelection();
      }

      if (this.selectedTaskId !== null) {
        this.hidePopover();
      }

      this.selectedUserId = data.taskData.user_id;
      this.selectedTaskId = data.taskId;
      this.lastTaskData = data;

      this.fetchScreenshotCount(data.taskData);

      // Track activity block click
      const rowPosition = this.data.findIndex((row) => row.id === data.taskData.user_id);

      this.trackAnalytics('Timeline Activity block clicked', {
        activity_type: data.taskData.type,
        row_position: rowPosition,
        has_screenshots: !!data.taskData.screenshot_count,
        duration_seconds: dayjs(data.taskData.end_date).diff(dayjs(data.taskData.start_date), 'seconds'),
        view_type: this.isUserMode ? 'user' : 'team',
      });

      const userSpan = this.$refs[`span-${data.taskData.user_id}`]?.[0];

      if (!userSpan) {
        return;
      }

      // IMPORTANT: Understanding the coordinate system:
      // 1. The popover is placed INSIDE the scrollable timeline container (#timeline-wrapper)
      // 2. The popover uses ABSOLUTE positioning within that container
      // 3. When the container scrolls, the popover automatically moves with it
      // 4. We need to provide the ABSOLUTE position within the container, NOT viewport-relative
      // 5. The span element (timeline-span-24) represents the full 24-hour timeline

      const timelineWrapper = this.$refs.timelineWrapper;
      const spanRect = userSpan.getBoundingClientRect();
      const timelineRect = timelineWrapper.getBoundingClientRect();
      // Calculate task position within the span element
      // The span width represents the full 24-hour timeline width
      const spanWidth = spanRect.width;
      const taskStartX = (spanWidth * data.position.startPercent) / 100;
      const taskWidth = (spanWidth * data.position.widthPercent) / 100;
      const taskCenterX = taskStartX + taskWidth / 2;
      // Calculate absolute position within the scrollable container
      // spanRect.left gives us the span's current position in the viewport
      // We need to convert this to absolute position within the container
      const spanAbsoluteLeft = spanRect.left - timelineRect.left + timelineWrapper.scrollLeft;
      const x = spanAbsoluteLeft + taskCenterX;
      // Use the clicked element's actual rect to position popover close to the bar
      const taskRect = data.position.rect;
      const y = taskRect.y - timelineRect.top + timelineWrapper.scrollTop;
      const bottomY = taskRect.bottom - timelineRect.top + timelineWrapper.scrollTop;

      this.popover = {
        data,
        position: { x, y, bottomY },
      };

      // Add document click listener with delay to prevent immediate closure
      // Only add if not already attached to prevent listener accumulation
      if (!this.isDocListenerAttached) {
        setTimeout(() => {
          document.addEventListener('click', this.handleDocumentClick);
          this.isDocListenerAttached = true;
        }, 100);
      }
    },
    hidePopover() {
      this.selectedTaskId = null;
      this.selectedUserId = null;
      this.popover = null;
      this.screenshotCount = null;
      document.removeEventListener('click', this.handleDocumentClick);
      this.isDocListenerAttached = false;
    },
    clearSelection() {
      this.hidePopover();
      this.lastTaskData = null;
    },
    formatTaskTime(dateString) {
      const time = dayjs.utc(`${dateString.slice(0, 23)}Z`);
      const seconds = time.second();

      return seconds === 59 ? time.add(1, 'second').format('h:mma') : time.format('h:mma');
    },
    formatTaskDuration(endDate, startDate) {
      return formatDuration(dayjs.utc(endDate).diff(dayjs.utc(startDate), 's') + 1, { showSeconds: 'never' });
    },
    formatDate(dateString) {
      return dayjs(dateString).format('ddd, MMM D, YYYY');
    },
    toggleSort(sortBy) {
      if (this.sort.sort_by === sortBy) {
        const newOrder = this.sort.sort_order === 'asc' ? 'desc' : 'asc';

        this.$emit('sort', { sort_by: sortBy, sort_order: newOrder });
      } else {
        this.$emit('sort', { sort_by: sortBy, sort_order: 'desc' });
      }
    },
    sortArrow(name) {
      if (this.sort.sort_by !== name) {
        return '';
      }

      return this.sort.sort_order === 'asc' ? 'north' : 'south';
    },
    groupConsecutiveIntervals(rows) {
      const groupIntervals = (intervals, isActiveTime) => {
        if (!intervals?.length) {
          return [];
        }

        let currentGroup = this.createInitialGroup(intervals[0], isActiveTime);
        const grouped = [];

        for (let i = 1; i < intervals.length; i++) {
          const current = intervals[i];
          const currentStart = dayjs(current.starts_at);
          const groupEnd = dayjs(currentGroup.ends_at);

          if (currentStart.isSame(groupEnd)) {
            // Extend current group
            currentGroup.ends_at = current.ends_at;

            // Preserve status from current item if it exists
            if (current.status) {
              currentGroup.status = current.status;
            }

            if (isActiveTime) {
              if (currentGroup.activity_levels) {
                currentGroup.activity_levels.pop();
                currentGroup.activity_levels.push(
                  { activity_level: current.activity_level, starts_at: current.starts_at, ends_at: current.ends_at },
                  { activity_level: current.activity_level, starts_at: current.ends_at },
                );
              }
            } else {
              if (currentGroup.tracked !== undefined && current.tracked !== undefined) {
                currentGroup.tracked += current.tracked;
              }
            }

            if (current.project_name && currentGroup.project_names) {
              currentGroup.project_names = [...new Set([...currentGroup.project_names, current.project_name])];
            }

            if (current.project_id !== undefined) {
              currentGroup.project_ids = [...(currentGroup.project_ids || []), current.project_id];
            }
          } else {
            // Start new group
            grouped.push(currentGroup);
            currentGroup = this.createInitialGroup(current, isActiveTime);
          }
        }

        if (currentGroup) {
          grouped.push(currentGroup);
        }

        return grouped;
      };

      return rows.map((row) => {
        if (!row.metrics) {
          return row;
        }

        const updatedMetrics = Object.fromEntries(
          Object.keys(row.metrics).map((type) => [type, groupIntervals(row.metrics[type], type === 'active_time')]),
        );

        return { ...row, metrics: updatedMetrics };
      });
    },
    createInitialGroup(item, isActiveTime) {
      const base = {
        ...item,
        starts_at: item.starts_at,
        ends_at: item.ends_at,
        ...(item.project_name && { project_names: [item.project_name] }),
        ...(item.project_id !== undefined && { project_ids: [item.project_id] }),
      };

      if (isActiveTime) {
        return {
          ...base,
          activity_levels: [
            { activity_level: item.activity_level, starts_at: item.starts_at, ends_at: item.ends_at },
            { activity_level: item.activity_level, starts_at: item.ends_at },
          ],
        };
      }

      return base;
    },
    formatTime(minutes) {
      if (minutes === null || minutes === undefined) {
        return '–';
      }
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;

      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },
    async ensureLayout() {
      await this.$nextTick();
      // Use requestAnimationFrame to ensure DOM has been painted
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const spans = this.data.map((row) => this.$refs[`span-${row.id}`]?.[0]).filter(Boolean);

      if (!spans.length) {
        return;
      }

      // Force layout recalculation by reading offsetWidth
      spans.forEach((span) => span.offsetWidth);

      const widths = spans.map((span) => span.getBoundingClientRect().width);
      const newWidth = Math.max(...widths);

      this.width = newWidth;
    },
    // Debounce chart updates via rAF to batch rapid watcher triggers
    scheduleUpdate() {
      if (this.updateChartTimer) {
        cancelAnimationFrame(this.updateChartTimer);
      }
      this.updateChartTimer = requestAnimationFrame(() => {
        this.updateChart();
      });
    },
    async updateChart() {
      await this.ensureLayout();
      if (!this.timeGapsCache) {
        this.timeGapsCache = this.computedTimeGaps;
      }
    },
    handleWidthMismatch() {
      // If SVG detects container is wider, force a width recalculation
      this.$nextTick(() => {
        this.ensureLayout();
      });
    },
    async fetchScreenshotCount(taskData) {
      if (!taskData.project_ids || taskData.project_ids.length === 0) {
        this.screenshotCount = 0;

        return;
      }

      try {
        const response = await ScreensResource.count(
          {},
          {
            project_ids: [...new Set(taskData.project_ids)],
            user_id: taskData.user_id_for_screens,
            // Strip milliseconds and timezone offset from dates
            // "2025-01-01T00:00:00.000+00:00" → "2025-01-01 00:00:00"
            from: taskData.start_date.replace('T', ' ').replace(/\..*$/, ''),
            to: taskData.end_date.replace('T', ' ').replace(/\..*$/, ''),
            time_zone: this.timeZone,
            organization_id: this.$store.state.organization.id,
          },
          { abortReload: true },
        );

        this.screenshotCount = response.count;

        this.$nextTick(() => {
          if (this.$refs.timelinePopover) {
            this.$refs.timelinePopover.reposition();
          }
        });
      } catch (error) {
        this.screenshotCount = 0;
      }
    },
    memberTimelineUrl(row) {
      const isoWeekday = toMomentIsoWeekday(this.$store.state.organization.start_week_on);
      const now = moment.tz(this.$store.getters.filters.date.from, this.timeZone);
      const from = now.clone().startOfIsoWeek(isoWeekday).format('YYYY-MM-DD');
      const to = now.clone().endOfIsoWeek(isoWeekday).format('YYYY-MM-DD');

      return `${window.location.pathname}?tab=timeline&user_id=${row.id}&from=${from}&to=${to}`;
    },
    handleMemberClick(row, event) {
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        return;
      }
      event.preventDefault();
      const rowIndex = this.data.findIndex((r) => r.id === row.id);

      this.trackAnalytics('Timeline Avatar clicked', {
        row_position: rowIndex,
        user_id: row.id,
        user_name: row.name,
        view_type: this.isUserMode ? 'user' : 'team',
      });

      this.$emit('timeline-user-selected', row.id);
    },
  },
};
</script>
